"use client";

import { toast } from "sonner";
import { useContext, useState } from "react";
import { logEvent } from "@/service/logs/log";
import { ResourceContext } from "@/contexts/resource-context";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { PictureAsset } from "@/components/common/picture-asset";
import { ArrowRightIcon, XIcon } from "lucide-react";
import { PracticeExamDialog } from "@/components/exams/practice-exam/practice-exam-dialog";
import { clientPost } from "@/service/client-api/client-api";
import { createPath } from "@/service/routing/routing";
import { SuperLink } from "@/components/common/super-link";
import { useRouter } from "@bprogress/next";
import dayjs from "dayjs";

export const PracticeExamBanner = ({
  closeBanner,
  practiceExamStatus,
}: {
  closeBanner?: () => void;
  practiceExamStatus: ExamPracticeExamStatusVM;
}) => {
  const { logView } = useContext(ResourceContext);
  const router = useRouter();
  const [loading, setLoading] = useState(false);
  const [confirmationOpen, setConfirmationOpen] = useState(false);

  if (!practiceExamStatus.startDate || !practiceExamStatus.endDate) return null;

  // Potwierdzenie pokazuje się dopiero po udanym zapisie. Odświeżenie stanu z serwera („Zapisz się"
  // → „Sprawdź") czeka na zamknięcie dialogu — inaczej przerysowanie banera zdejmowało popup.
  async function onSubscribe() {
    if (loading) return;

    // kliknięcie logujemy od razu (zdarzenie mierzy klik, nie skuteczność zapisu)
    handleLinkClick("sign up");

    try {
      setLoading(true);
      await clientPost("exam.practice_exam.subscribe");
      logEvent("practice_exam_subscribe", logView);
      setConfirmationOpen(true);
    } catch (error) {
      console.error("Subscription failed", error);
      toast.error("Nie udało się zapisać na próbny LEK. Spróbuj ponownie.");
    } finally {
      setLoading(false);
    }
  }

  const handleLinkClick = (type: string) => {
    logEvent("home_banner_button_click", logView, {
      banner_type: "practice_exam",
      banner_button_type: type,
    });
  };

  return (
    <>
      <PracticeExamDialog
        practiceExamStatus={practiceExamStatus}
        open={confirmationOpen}
        onOpenChange={setConfirmationOpen}
        onClose={() => router.refresh()}
      />
      <div
        className={
          "relative my-10 w-full items-center gap-14 overflow-hidden rounded-2xl bg-[#1f1f1f] md:flex md:h-[200px]"
        }
      >
        {closeBanner && (
          <div
            className={
              "absolute top-0 right-0 z-10 cursor-pointer p-4 md:top-2 md:right-2"
            }
          >
            <XIcon
              size={20}
              className={"text-white"}
              onClick={() => {
                closeBanner();
              }}
            />
          </div>
        )}

        <div
          className={
            "relative z-2 flex w-full flex-col justify-between p-6 md:flex-row md:p-10"
          }
        >
          <div className="flex flex-col gap-6">
            <span className="text-xl font-semibold text-white [text-shadow:_0px_0px_23px_rgb(0_0_0_/_0.50)] md:font-bold lg:text-2xl">
              Próbny LEK <span className="hidden md:inline">z Remedium!</span>
            </span>
            <div
              className={
                "relative z-2 flex flex-col gap-4 pb-2 md:flex-row md:gap-11"
              }
            >
              <span className="max-w-[450px] text-left text-sm font-semibold text-white md:font-normal">
                Runda {practiceExamStatus.positionInGroup} już{" "}
                {dayjs(practiceExamStatus.startDate).format("D MMMM o HH:mm")}!
                Weź udział i&nbsp;sprawdź
                <br /> jak wypadniesz na tle innych.{" "}
                {!practiceExamStatus.isSubscribed &&
                  "Zapisz się by być na bieżąco!"}
              </span>
              {!practiceExamStatus.isSubscribed &&
              practiceExamStatus.startDate ? (
                <Button
                  variant={"default"}
                  className={"w-full rounded-full disabled:opacity-50 md:w-fit"}
                  onClick={onSubscribe}
                  disabled={loading}
                >
                  Zapisz się{" "}
                  {loading ? <Spinner size="small" /> : <ArrowRightIcon />}
                </Button>
              ) : (
                <Button
                  variant={"default"}
                  className={"w-full rounded-full disabled:opacity-50 md:w-fit"}
                  onClick={() => handleLinkClick("examsLink")}
                  asChild
                >
                  <SuperLink href={createPath("exam.practice_exam")}>
                    Sprawdź <ArrowRightIcon />
                  </SuperLink>
                </Button>
              )}
            </div>
          </div>
          <div className="-mb-6 flex items-end justify-center md:hidden">
            <PictureAsset
              source={
                "https://cdn.remedium.md/image/common/2026/01/05/12/lek-baner-mobile-1-png-695ba3724f9a1"
              }
              ext={"png"}
              alt={"Próbny lek z Remedium"}
              className={"max-w-[328px] object-cover object-center"}
            />
          </div>
        </div>
        <PictureAsset
          source={
            "https://cdn.remedium.md/image/common/2026/01/05/12/egz-baner2-1-png-695ba3b502f83"
          }
          ext={"png"}
          alt={"Próbny lek z Remedium"}
          className={
            "absolute inset-0 hidden h-full w-full object-cover md:block"
          }
        />

        <PictureAsset
          source={
            "https://cdn.remedium.md/image/common/2026/01/05/12/egz-baner-mobile-1-png-695ba39953181"
          }
          ext={"png"}
          alt={"Próbny lek z Remedium"}
          className={
            "absolute inset-0 h-full w-full object-cover object-center md:hidden"
          }
        />
      </div>
    </>
  );
};
